import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AuthenticationService } from '../../auth/authentication.service';
import { AjsResponse, wrapAjs } from '../ajs-response';
import { JobOutput } from './import-exports-api.types';

/**
 * ImportExportsApiService (Angular 18) — Import/Export microservice for
 * export job executions, outputs, and field/type catalog lookups.
 *
 * Replaces apps/b2b-ui/common/services/api/import/importExportsApi.service.js.
 *
 * Endpoint base hardcoded to /service/import/api/export (legacy provider's
 * setEndpoint() value).
 *
 * Legacy quirks preserved:
 *   - `x-tenant-id` header captured ONCE at construction from the `user`
 *     cookie.
 *   - `getDownloadUrl(id)` returns a `string` synchronously.
 *   - `create(jobId, date)` forwards `date.toISOString()` as `from` query
 *     param only when `date` is truthy (legacy `date && date.toISOString()`).
 */
const BASE = '/service/import/api/export';

@Injectable({ providedIn: 'root' })
export class ImportExportsApiService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthenticationService);

  private readonly tenantId: string | undefined = this.readTenantId();

  private readTenantId(): string | undefined {
    const userCookie = this.auth.getCookie<{ tenantId?: string }>('user') ?? {};
    return userCookie.tenantId;
  }

  private get tenantHeaders(): HttpHeaders {
    return new HttpHeaders({ 'x-tenant-id': this.tenantId ?? '' });
  }

  list(
    jobId: number | string,
    page?: number | null,
    size?: number | null,
    sort?: string | null,
  ): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${BASE}/${jobId}`, {
        headers: this.tenantHeaders,
        params: {
          page: page ?? '',
          size: size ?? '',
          sort: sort ?? '',
        },
      }),
    );
  }

  create(jobId: number | string, date?: Date): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(
        `${BASE}/${jobId}`,
        { jobId },
        {
          headers: this.tenantHeaders,
          params: { from: date ? date.toISOString() : '' },
        },
      ),
    );
  }

  get(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${BASE}/execution/${id}`, { headers: this.tenantHeaders }));
  }

  getOutputs(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${BASE}/execution/outputs/${id}`, { headers: this.tenantHeaders }));
  }

  getTypes(): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${BASE}/output/types`, { headers: this.tenantHeaders }));
  }

  getFieldsByType(type: string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${BASE}/output/fields/${type}`, { headers: this.tenantHeaders }));
  }

  getJobOutputs(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${BASE}/outputs/${id}`, { headers: this.tenantHeaders }));
  }

  createOrUpdateJobOutput(jobOutput: JobOutput): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.post<unknown>(`${BASE}/outputs`, jobOutput, { headers: this.tenantHeaders }));
  }

  /** Synchronous: returns the raw URL string used as an anchor href. */
  getDownloadUrl(id: number | string): string {
    return `${BASE}/download/${id}`;
  }

  remove(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.delete<unknown>(`${BASE}/${id}`, { headers: this.tenantHeaders }));
  }
}
